<template>
  <div>
    <div class="title-text mb-3">
      <b>Bens Sinistrados</b>
    </div>
    <div class="row g-3 mb-3 mt-3" v-if="!isReadonlyStage">
      <div class="col-md-6">
        <label for="bemSubtraido" class="form-label">Bem Subtraído</label>
        <Dropdown
          id="bemSubtraido"
          v-model="bemSubtraido.id"
          :options="gdmTipos"
          optionValue="Id"
          optionLabel="Description"
          placeholder="Selecione"
          class="form-control p-0"
          :disabled="isReadonlyStage"
          @change="clearBemInputs"
          :loading="isLoadingGdmTipos"
        />
      </div>

      <div
        class="col-md-6"
        :class="{
          'field-required': $v.bemSubtraido.valor.$invalid,
        }"
      >
        <label for="bemSubtraidoValor" class="form-label">Valor</label>
        <InputNumber
          id="bemSubtraidoValor"
          v-model="bemSubtraido.valor"
          mode="currency"
          currency="BRL"
          locale="pt-BR"
          :minFractionDigits="2"
          :maxFractionDigits="2"
          :disabled="!bemSubtraido.id || isReadonlyStage"
          class="form-control p-0"
        />
      </div>
      <div
        class="col-md-6"
        :class="{
          'field-required': $v.bemSubtraido.descricao.$invalid,
        }"
      >
        <label for="bemSubtraidoDescricao" class="form-label">Descrição</label>
        <InputText
          id="bemSubtraidoDescricao"
          v-model="bemSubtraido.descricao"
          class="form-control"
          :disabled="!bemSubtraido.id || isReadonlyStage"
        />
      </div>

      <div
        class="col-md-6"
        :class="{
          'field-required': $v.bemSubtraido.IMEI.$invalid,
        }"
      >
        <label for="bemSubtraidoIMEI" class="form-label">IMEI</label>
        <InputText
          id="bemSubtraidoIMEI"
          v-model="bemSubtraido.IMEI"
          class="form-control"
          :disabled="isReadonlyStage || !isSelectedBemSubtraidoCelular"
        />
      </div>

      <div
        class="col-md-6"
        :class="{
          'field-required': $v.bemSubtraido.dataBloqueioIMEI.$invalid,
        }"
      >
        <label for="bemSubtraidoDataBloqueioIMEI" class="form-label"
          >Data de Bloqueio do IMEI</label
        >
        <Calendar
          id="bemSubtraidoDataBloqueioIMEI"
          v-model="bemSubtraido.dataBloqueioIMEI"
          dateFormat="dd/mm/yy"
          selectionMode="single"
          :showOnFocus="true"
          :showTime="false"
          :showSeconds="false"
          :manualInput="false"
          :maxDate="maxDate"
          class="form-control p-0"
          placeholder="DD/MM/AAAA"
          :disabled="isReadonlyStage || !isSelectedBemSubtraidoCelular"
        />
      </div>

      <div class="col-md-6">
        <label for="bemSubtraidoProtocoloIMEI" class="form-label"
          >Protocolo IMEI</label
        >
        <InputText
          id="bemSubtraidoProtocoloIMEI"
          v-model="bemSubtraido.protocoloIMEI"
          class="form-control"
          :disabled="isReadonlyStage || !isSelectedBemSubtraidoCelular"
        />
      </div>

      <div class="col-md-6">
        <Button
          @click="adicionarBemLista"
          label="Adicionar"
          :disabled="
            !hasBemBeenSelected || isReadonlyStage || $v.bemSubtraido.$invalid
          "
        />
      </div>
      <div class="col-md-6">
        <span for="estimativaPrejuizo" class="form-label"
          >Estimativa de Prejuízo</span
        >
        <InputNumber
          id="estimativaPrejuizo"
          :minFractionDigits="2"
          :maxFractionDigits="2"
          v-model="estimativaPrejuizo"
          mode="currency"
          currency="BRL"
          class="form-control p-0"
          locale="pt-BR"
          disabled
        />
      </div>
    </div>

    <div class="row g-3 mb-3 mt-3" v-if="isReadonlyStage">
      <div class="col-md-6">
        <label for="estimativaPrejuizo" class="form-label"
          >Estimativa de Prejuízo</label
        >
        <InputNumber
          id="estimativaPrejuizo"
          :minFractionDigits="2"
          :maxFractionDigits="2"
          v-model="estimativaPrejuizo"
          mode="currency"
          currency="BRL"
          class="form-control p-0"
          locale="pt-BR"
          disabled
        />
      </div>
    </div>
    <div class="row g-3 mb-3 mt-3">
      <DataTable :value="listaBensSubtraidos">
        <Column field="GDMTipoId" header="Bem Subtraído">
          <template #body="slotProps">
            {{ getDescriptionFromGdmTipo(slotProps.data.GDMTipoId) }}
          </template>
        </Column>
        <Column field="CompraValor" header="Valor" dataType="numeric">
          <template #body="slotProps">
            {{ formatCurrency(slotProps.data.CompraValor) }}
          </template>
        </Column>
        <Column field="DescricaoGeral" header="Descrição"></Column>
        <Column field="IMEI" header="IMEI"></Column>
        <Column field="BloqueioIMEIData" header="Data de Bloqueio do IMEI">
          <template #body="slotProps">
            {{ bemDisplayDate(slotProps.data.BloqueioIMEIData) }}
          </template>
        </Column>
        <Column field="BloqueioIMEIRef" header="Protocolo do IMEI"></Column>
        <Column v-if="!isReadonlyStage">
          <template #body="slotProps">
            <i
              class="fas fa-times pointer"
              :disabled="isReadonlyStage"
              @click="removerBemLista(slotProps.data)"
            ></i>
          </template>
        </Column>
        <template #footer v-if="$v.listaBensSubtraidos.$invalid">
          <p>Tem de ser adicionado pelo menos 1 item.</p>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { required, requiredIf } from 'vuelidate/lib/validators';

export default {
  emits: ['update:listaBensSubtraidos', 'update:estimativaPrejuizo'],
  props: {
    processoGDM: {
      type: Object,
      required: true,
    },
    gdmTipos: {
      type: Array,
      required: true,
    },
    isReadonlyStage: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLoadingGdmTipos: {
      type: Boolean,
      required: true,
    },
    maxDate: {
      type: Date,
      required: true,
    },
  },
  computed: {
    estimativaPrejuizo() {
      let soma = 0;

      this.listaBensSubtraidos.forEach((bem) => {
        if (bem.CompraValor) {
          soma += parseFloat(bem.CompraValor);
        }
      });
      console.log('estimativaPrejuizo', soma);
      return soma;
    },
    hasBemBeenSelected() {
      console.log('hasBemBeenSelected', this.bemSubtraido.id);
      return this.bemSubtraido.id !== null;
    },
    isSelectedBemSubtraidoCelular() {
      console.log('isSelectedBemSubtraidoCelular', this.bemSubtraido.id);
      return this.bemSubtraido.id === this.CELULAR;
    },
  },
  data() {
    return {
      CELULAR: 17,
      bemSubtraido: {
        fakeId: null,
        id: null,
        valor: 0,
        descricao: null,
        IMEI: null,
        dataBloqueioIMEI: null,
        protocoloIMEI: null,
      },
      listaBensSubtraidos: [],
    };
  },
  validations() {
    return {
      bemSubtraido: {
        descricao: {
          requiredIf: requiredIf(() => this.hasBemBeenSelected),
        },
        valor: {
          requiredIf: requiredIf(() => this.hasBemBeenSelected),
        },
        IMEI: {
          requiredIf: requiredIf(() => this.isSelectedBemSubtraidoCelular),
        },
        dataBloqueioIMEI: {
          requiredIf: requiredIf(() => this.isSelectedBemSubtraidoCelular),
        },
      },
      listaBensSubtraidos: { required },
    };
  },
  mounted() {
    console.log('onMounted BensSinistrados - processoGDM: ', this.processoGDM);
    if (this.processoGDM) {
      const childs = this.processoGDM.ChildrenProcessoGDMDTOs ?? []; // Here we dont care about the children, we just want to add them to the list

      if (childs && childs.length > 0) {
        childs.forEach((child) => {
          child.FakeId = this.generateFakeId();
          if (child.BloqueioIMEIData) {
            child.BloqueioIMEIData = dayjs(child.BloqueioIMEIData);
          }
        });

        this.listaBensSubtraidos.push(...childs);
      }
    }
    this.$emit('update:listaBensSubtraidos', this.listaBensSubtraidos);
    this.$emit('update:estimativaPrejuizo', this.estimativaPrejuizo);
  },
  methods: {
    formatCurrency(value) {
      const formato = {
        minimumFractionDigits: 2,
        style: 'currency',
        currency: 'BRL',
      };
      return value.toLocaleString('pt-BR', formato);
    },
    adicionarBemLista() {
      // const gdmDescricao = ;
      // console.log(gdmDescricao);
      if (!this.hasBemBeenSelected) {
        return;
      }

      const bemAdicionar = {
        FakeId: this.generateFakeId(),
        GDMTipoId: this.bemSubtraido.id,
        CompraValor: this.bemSubtraido.valor,
        DescricaoGeral: this.bemSubtraido.descricao,
        IMEI: this.bemSubtraido.IMEI,
        BloqueioIMEIData: this.bemSubtraido.dataBloqueioIMEI,
        BloqueioIMEIRef: this.bemSubtraido.protocoloIMEI,
      };

      this.listaBensSubtraidos.push(bemAdicionar);
      this.$emit('update:listaBensSubtraidos', this.listaBensSubtraidos);
      this.$emit('update:estimativaPrejuizo', this.estimativaPrejuizo);

      // Limpar inputs
      this.bemSubtraido.id = null;
      this.clearBemInputs();
    },
    removerBemLista(bem) {
      console.log('bem to remove', bem);
      this.listaBensSubtraidos = this.listaBensSubtraidos.filter(
        (x) => x.FakeId !== bem.FakeId
      );
      this.$emit('update:listaBensSubtraidos', this.listaBensSubtraidos);
      this.$emit('update:estimativaPrejuizo', this.estimativaPrejuizo);
    },
    clearBemInputs() {
      this.bemSubtraido.valor = 0;
      this.bemSubtraido.descricao = null;
      this.bemSubtraido.IMEI = null;
      this.bemSubtraido.dataBloqueioIMEI = null;
      this.bemSubtraido.protocoloIMEI = null;
    },
    bemDisplayDate(date) {
      if (!date) return;
      return dayjs(date).format('DD/MM/YYYY');
    },
    getDescriptionFromGdmTipo(id) {
      return (
        this.gdmTipos.find((x) => x.Id === id)?.Description ??
        `Not found ${id} in GDM Tipos`
      );
    },
    generateFakeId() {
      return uuidv4();
    },
  },
};
</script>

<style></style>
